import { useState } from 'react';
import { Plus, Pencil, Trash2, X, SlidersHorizontal } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Modal } from '@/components/ui/modal';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useModifierGroups,
  useCreateModifierGroup,
  useUpdateModifierGroup,
  useDeleteModifierGroup,
} from '@/hooks/use-modifiers';
import { toast } from '@/stores/toast-store';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

interface ModifierRow {
  id?: string;
  name: string;
  price: string;
}

interface GroupForm {
  id: string | null;
  name: string;
  description: string;
  required: boolean;
  minSelections: string;
  maxSelections: string;
  modifiers: ModifierRow[];
}

const emptyForm: GroupForm = {
  id: null,
  name: '',
  description: '',
  required: false,
  minSelections: '0',
  maxSelections: '',
  modifiers: [{ name: '', price: '0' }],
};

export function ModifierListPage() {
  const { data, isLoading } = useModifierGroups();
  const permissions = usePermissions();
  const canAdd = permissions.has('modifiers.add');
  const canEdit = permissions.has('modifiers.edit');
  const canDelete = permissions.has('modifiers.delete');
  const allGroups = data?.data ?? [];

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const sortedGroups = [...allGroups].sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  const total = sortedGroups.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const paginatedGroups = sortedGroups.slice((page - 1) * perPage, page * perPage);

  const createGroup = useCreateModifierGroup();
  const updateGroup = useUpdateModifierGroup();
  const deleteGroup = useDeleteModifierGroup();

  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState<GroupForm>(emptyForm);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; linkedCount: number } | null>(null);

  const updateField = <K extends keyof GroupForm>(key: K, value: GroupForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const openCreate = () => {
    setForm(emptyForm);
    setFormError('');
    setShowEditor(true);
  };

  const openEdit = (group: any) => {
    setForm({
      id: group.id,
      name: group.name ?? '',
      description: group.description ?? '',
      required: (group.minSelections ?? 0) >= 1,
      minSelections: String(group.minSelections ?? 0),
      maxSelections: group.maxSelections == null ? '' : String(group.maxSelections),
      modifiers:
        (group.modifiers ?? []).length > 0
          ? group.modifiers.map((m: any) => ({ id: m.id, name: m.name, price: String(m.price ?? 0) }))
          : [{ name: '', price: '0' }],
    });
    setFormError('');
    setShowEditor(true);
  };

  const addModifierRow = () => setForm((f) => ({ ...f, modifiers: [...f.modifiers, { name: '', price: '0' }] }));

  const updateModifierRow = (index: number, key: keyof ModifierRow, value: string) =>
    setForm((f) => ({
      ...f,
      modifiers: f.modifiers.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    }));

  const removeModifierRow = (index: number) =>
    setForm((f) => ({ ...f, modifiers: f.modifiers.filter((_, i) => i !== index) }));

  const handleSave = async () => {
    setFormError('');
    if (!form.name.trim()) {
      setFormError('Group name is required');
      return;
    }
    const cleanModifiers = form.modifiers
      .filter((m) => m.name.trim())
      .map((m, i) => ({
        ...(m.id ? { id: m.id } : {}),
        name: m.name.trim(),
        price: Number(m.price) || 0,
        sortOrder: i,
      }));
    if (cleanModifiers.length === 0) {
      setFormError('Add at least one modifier option');
      return;
    }

    // Required == at least one selection. When required and the user left min
    // at 0 we default to 1; optional groups always have min 0.
    const min = form.required ? Math.max(1, Number(form.minSelections) || 1) : 0;
    const max = form.maxSelections.trim() === '' ? null : Number(form.maxSelections);
    if (max !== null && (!Number.isFinite(max) || max < 1)) {
      setFormError('Max selections must be a positive number or left blank for unlimited');
      return;
    }
    if (max !== null && max < min) {
      setFormError('Max selections cannot be less than min selections');
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      minSelections: min,
      maxSelections: max,
      modifiers: cleanModifiers,
    };

    try {
      if (form.id) {
        await updateGroup.mutateAsync({ id: form.id, ...payload });
        toast.success(`Updated "${payload.name}"`);
      } else {
        await createGroup.mutateAsync(payload);
        toast.success(`Created "${payload.name}"`);
      }
      setShowEditor(false);
    } catch (e: any) {
      setFormError(e?.error?.message || 'Failed to save modifier group');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteGroup.mutateAsync({ id });
      toast.success(`Deleted "${name}"`);
    } catch (e: any) {
      if (e?.error?.code === 'MODIFIER_GROUP_HAS_PRODUCTS') {
        setDeleteTarget({ id, name, linkedCount: e.error.linkedProductCount ?? 0 });
        return;
      }
      toast.error(e?.error?.message || `Failed to delete "${name}"`);
    }
  };

  const handleConfirmCascadeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteGroup.mutateAsync({ id: deleteTarget.id, cascade: true });
      toast.success(
        `Deleted "${deleteTarget.name}" and unassigned ${deleteTarget.linkedCount} product${
          deleteTarget.linkedCount === 1 ? '' : 's'
        }`,
      );
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to delete modifier group');
    }
  };

  const ruleLabel = (group: any) => {
    const min = group.minSelections ?? 0;
    const max = group.maxSelections;
    if (min >= 1 && max != null && min === max) return `Choose ${min}`;
    if (max != null) return `Choose ${min}-${max}`;
    return min >= 1 ? `Choose ${min}+` : 'Optional';
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Modifiers">
        {canAdd && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create
          </button>
        )}
      </PageHeader>

      <div className="rounded-lg border border-border bg-white">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">Loading...</div>
        ) : allGroups.length === 0 ? (
          <EmptyState
            icon={SlidersHorizontal}
            title="No modifier groups yet"
            description="Click Create to add your first modifier group."
          />
        ) : (
          <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[480px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Name
                  </th>
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Rule
                  </th>
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Options
                  </th>
                  <th className="px-5 py-3.5 text-right text-[13px] font-semibold tracking-wide text-gray-700 uppercase w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedGroups.map((group: any) => (
                  <tr
                    key={group.id}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-5 py-3 text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {group.name}
                        {(group.minSelections ?? 0) >= 1 ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                            Required
                          </span>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                            Optional
                          </span>
                        )}
                      </div>
                      {group.description && (
                        <div className="mt-0.5 text-xs text-muted-foreground">{group.description}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{ruleLabel(group)}</td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">
                      {(group.modifiers ?? [])
                        .slice(0, 4)
                        .map((m: any) => m.name)
                        .join(', ')}
                      {(group.modifiers ?? []).length > 4 ? `, +${group.modifiers.length - 4} more` : ''}
                      {(group.modifiers ?? []).length === 0 ? '—' : ''}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => openEdit(group)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                            title="Edit modifier group"
                            aria-label="Edit modifier group"
                          >
                            <Pencil className="h-4 w-4 text-primary" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(group.id, group.name)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                            title="Delete modifier group"
                            aria-label="Delete modifier group"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!isLoading && allGroups.length > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={handlePerPageChange}
        />
      )}

      <Modal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        title={form.id ? 'Edit Modifier Group' : 'Create Modifier Group'}
        widthClass="max-w-[640px]"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setShowEditor(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createGroup.isPending || updateGroup.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {form.id ? 'Save Changes' : 'Create Group'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{formError}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Group Name</label>
            <input
              type="text"
              placeholder="e.g. Cheese, Size, Toppings"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              autoFocus
              className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description (optional)</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(e) => updateField('required', e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              Required (customer must choose)
            </label>
            {form.required && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Min selections</label>
                <input
                  type="number"
                  min={1}
                  value={form.minSelections}
                  onChange={(e) => updateField('minSelections', e.target.value)}
                  className="h-9 w-28 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Max selections</label>
              <input
                type="number"
                min={1}
                placeholder="Unlimited"
                value={form.maxSelections}
                onChange={(e) => updateField('maxSelections', e.target.value)}
                className="h-9 w-28 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Modifier Options</label>
              <button
                onClick={addModifierRow}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted"
              >
                <Plus className="h-3.5 w-3.5" />
                Add option
              </button>
            </div>
            <div className="space-y-2">
              {form.modifiers.map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Option name (e.g. Swiss)"
                    value={m.name}
                    onChange={(e) => updateModifierRow(i, 'name', e.target.value)}
                    className="h-9 min-w-0 flex-1 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">+$</span>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={m.price}
                      onChange={(e) => updateModifierRow(i, 'price', e.target.value)}
                      className="h-9 w-24 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary"
                    />
                  </div>
                  <button
                    onClick={() => removeModifierRow(i)}
                    disabled={form.modifiers.length === 1}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border hover:bg-red-50 disabled:opacity-40"
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Unassign products and delete "${deleteTarget?.name}"?`}
        message={
          deleteTarget
            ? `${deleteTarget.linkedCount} product${deleteTarget.linkedCount === 1 ? ' is' : 's are'} still linked to this modifier group. Continuing will remove this group from ${deleteTarget.linkedCount === 1 ? 'that product' : 'those products'} and then delete it. This cannot be undone.`
            : ''
        }
        confirmLabel="Unassign & delete"
        variant="danger"
        loading={deleteGroup.isPending}
        onConfirm={handleConfirmCascadeDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
